import { useRef, useEffect, useMemo, useState } from "react";
import { AlertService } from "../../../services/Alert";
import { QRCodeSVG } from 'qrcode.react';
import TextArea from "../../../components/form-controls/TextArea";
import { Controller, useForm } from "react-hook-form";
import Select from 'react-select'
import Button from "../../../components/form-controls/Button";
import { ShortLinksService } from "../../../services/ShortLinksService";
import { SubAccountService } from '../../../services/SubaccountService';
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import useModal from "../../../hooks/useModal";
import InvalidFeedback from "../../../components/InvalidFeedback";
import { useLocation } from "react-router-dom";
import { APPS } from "../../../constants";
import { useTranslation } from "react-i18next";
import Switch from "../../../components/form-controls/Switch";
import Input from "../../../components/form-controls/Input";

declare const bootstrap: any;

const templateCode = `
<script src="https://voices-dev.github.io/contacto_wa.js"></script>
<script>
    document.addEventListener("DOMContentLoaded", (event) =>  voicesWhatsApp("NUMBER","MESSAGE","TOKEN") )
</script>
`

interface EditLinkModalProps {
    state: { show: boolean, link: any };
    onRequestClose: (refresh: boolean) => void;
}

function EditLinkModal({ state, onRequestClose }: EditLinkModalProps) {

    const { t } = useTranslation();

    const { account } = useCurrentAccount();

    const modal = useRef<HTMLDivElement>(null);

    const { state: { channels } } = useLocation()

    useModal(modal, state.show)

    const defaultValues = {
        message: '',
        app: '',
        advanced_follow: false,
        label: '',
        trigger: '',
        sub_account_id: 0
    }

    const [subaAccounts, setsubAccounts] = useState([]);
    const opciones = [
        { label: 'Enviar a Subcuenta', value: 'sent_subaccount' },
        { label: 'Directo a agentes', value: 'sent_agent' },
        { label: 'Ninguno', value: 'none' }
    ];


    const { control, register, watch, setValue, handleSubmit, reset, formState: { errors, isValid, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues
    })
    const whatsappStandar = useMemo(() => channels.find(c => c.app.toUpperCase() === APPS.WHATSAPP_STANDAR), [channels])
    const whatsappBusiness = useMemo(() => channels.find(c => c.app.toUpperCase() === APPS.WHATSAPP_BUSINESS), [channels])
    const code = useMemo(() => {
        if (!state.link) return templateCode;

        const number = state.link.app === 'wa-business' ? whatsappBusiness.meta_phone_number : whatsappStandar.number;

        return templateCode
            .replace('NUMBER', number)
            .replace('MESSAGE', state.link.message)
            .replace('TOKEN', state.link.token)

    }, [state.link])

    const advancedFollowshow = watch('advanced_follow');
    const subAccountShow = watch('sub_account_id');
    const triggerShow = watch('trigger');

    const getSubAccounts = async () => {
        const { data } = await SubAccountService.getSubaccounts(account.id);
        setsubAccounts(data.sub_accounts.map(subaccount => ({
            value: subaccount.id,
            label: subaccount.name
        })));
    };

    const submit = async (data) => {
        try {
            data.advanced_follow = data.advanced_follow ? 1 : 0;
            await ShortLinksService.update(state.link.account_id, data.message, state.link.token, data.app, data.advanced_follow, data.label, data.trigger, data.sub_account_id)
            await onRequestClose(true)
            AlertService.toastSuccess('Se actualizó correctamente el enlace')
        } catch (error) {
            console.error(error);
            AlertService.error('¡Error!', error)
        }
    }


    const copyToClipboard = async (text) => {
        await navigator.clipboard.writeText(text);

        AlertService.toastSuccess('Texto copiado', 2000)
    }

    useEffect(() => {
        reset({
            message: state?.link?.message,
            app: state?.link?.app,
            advanced_follow: state?.link?.advanced_follow,
            label: state?.link?.label,
            trigger: state?.link?.trigger,
            sub_account_id: state?.link?.sub_account_id
        })
    }, [state.link])


    useEffect(() => {
        if (!advancedFollowshow) {
            setValue('advanced_follow', false);
            setValue('label', defaultValues.label);
            setValue('sub_account_id', defaultValues.sub_account_id);
            setValue('trigger', defaultValues.trigger);
        }
    }, [advancedFollowshow, setValue]);


    useEffect(() => {
        if (triggerShow !== 'sent_subaccount') {
            setValue('sub_account_id', 0);
        }
    }, [triggerShow, setValue]);

    useEffect(() => {
        if (!subAccountShow) {
            setValue('sub_account_id', 0);
        }
    }, [subAccountShow, setValue]);


    useEffect(() => {
        getSubAccounts();
    }, [account]);


    return (
        <div className="modal fade" ref={modal} data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content ">
                    <div className="modal-header">
                        <h4 className="modal-title">{t('platform.channels.edit-modal.edit-title')}</h4>
                        <button type="button" className="btn-close" onClick={() => onRequestClose(false)}></button>
                    </div>
                    <div className="modal-body">
                        <div className="text-center navbar-voices rounded-1 border p-4" >
                            <div className="mb-3">
                                <QRCodeSVG id={`qr-img-${state?.link?.token}`} value={`https://${state?.link?.link}`} size={200} />
                            </div>
                            <button type='button' className='btn btn-ghost-primary btn-lg' onClick={() => copyToClipboard(state?.link?.link)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.download-btn')}</button>
                        </div>

                        <hr />
                        <div className="mb-3">
                            <div className="d-flex align-self-center">
                                <div className="flex-grow-1 me-3">
                                    <label className="form-label" >{t('platform.channels.edit-modal.link-label')}</label>
                                    <input type="text" className="form-control" disabled defaultValue={state?.link?.link} />
                                </div>

                                <button type='button' className='btn btn-soft-primary align-self-end' onClick={() => copyToClipboard(state?.link?.link)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.copy-btn')}</button>
                            </div>


                        </div>
                        <hr />

                        <label className="form-label" >{t('platform.channels.edit-modal.insert-code-label')}</label>
                        <p>{t('platform.channels.edit-modal.insert-text')}</p>

                        <div className="mb-3">
                            <div className='navbar-voices rounded-1 border relative'>
                                <button type='button' className='btn btn-ghost-primary btn-lg fix-top-right' onClick={() => copyToClipboard(code)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.copy-text')} </button>
                                <pre className='monospace p-3' >
                                    {code}
                                </pre>
                            </div>
                        </div>

                        <hr />
                        <div className="mb-3">
                            <Switch label="Seguimiento Avanzado" {...register('advanced_follow')} />
                        </div>
                        {advancedFollowshow && (
                            <>
                                <div className="mb-3">
                                    <Input label='Etiqueta *' placeholder="Ingresa etiqueta" errors={errors.label} {...register('label', { required: true })} />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Disparador *</label>
                                    <Controller
                                        control={control}
                                        name='trigger'
                                        rules={{ required: true }}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <>
                                                <Select
                                                    options={opciones}
                                                    className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                    classNamePrefix='form-select-custom'
                                                    placeholder='Selecciona un disparador'
                                                    onChange={(selectedOption) => { onChange(selectedOption.value); }}
                                                    value={opciones.find(option => option.value === value) || null}
                                                />
                                                <InvalidFeedback error={error} />
                                            </>
                                        )}
                                    />
                                </div>
                                {triggerShow === 'sent_subaccount' && (
                                    <div className="mb-3">
                                        <label className="form-label">Subcuentas *</label>
                                        <Controller
                                            control={control}
                                            name='sub_account_id'
                                            rules={{ required: true }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <>
                                                    <Select
                                                        options={subaAccounts}
                                                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                        classNamePrefix='form-select-custom'
                                                        placeholder='Selecciona una subcuenta'
                                                        onChange={(selectedOption) => onChange(selectedOption.value)}
                                                        value={subaAccounts.find(option => option.value === value) || null}
                                                    />
                                                    <InvalidFeedback error={error} />
                                                </>
                                            )}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        <div className="mb-3">
                            <TextArea placeholder="Ingresa un mensaje" label={t('platform.channels.edit-modal.message-label')} errors={errors.message}  {...register('message', { required: true })} />
                        </div>

                        {/* <div className="mb-3">
                            <label className="form-label" >Canal *</label>
                            <select className={'form-control ' + (errors?.app ? 'is-invalid' : '')}  {...register('app', { required: true })}>
                                <option disabled value={''}>Elija una opción</option>
                                <option value={'waweb'}>WhatsApp Estandar</option>
                                <option value={'wa-business'}>WhatsApp Business</option>
                            </select>
                            <InvalidFeedback error={errors.app} />
                        </div> */}

                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-white btn-sm" onClick={() => onRequestClose(false)}>{t('btn-cancel')} </button>
                        <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting} onClick={handleSubmit(submit)}>{t('btn-save')}</Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default EditLinkModal